"use client";

import type { ExpansionPanelProperties } from "@jetblue/kit/expansion-panel";
import {
  Children,
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useCallback,
  useState,
} from "react";

type InfoAccordionAreaClientProperties = {
  allowMultipleOpen?: boolean | undefined;
  children?: ReactNode;
  variant?: "primary" | "secondary";
};

type ExpansionPanelWithMetadataProperties = ExpansionPanelProperties & {
  metadata?: { "@id"?: string };
};

type ChildWithChildren = ReactElement<{ children?: ReactNode }>;

export const InfoAccordionAreaClient = ({
  allowMultipleOpen = false,
  children,
  variant = "secondary",
}: InfoAccordionAreaClientProperties) => {
  const items = Children.toArray(children);
  const [openIds, setOpenIds] = useState<string[]>([]);

  const handleSingleOpenPanel = useCallback((id: string) => {
    setOpenIds((previous) => (previous.includes(id) ? [] : [id]));
  }, []);
  const handleMultipleOpenPanels = useCallback((id: string) => {
    setOpenIds((previous) =>
      previous.includes(id)
        ? previous.filter((openId) => openId !== id)
        : [...previous, id],
    );
  }, []);

  return (
    <>
      {items.map((child, index) => {
        if (!isValidElement<ChildWithChildren>(child)) {
          return null;
        }

        const wrappedChildren = Children.toArray(
          (child as ChildWithChildren).props.children,
        );
        const panelElement =
          wrappedChildren[1] as React.ReactElement<ExpansionPanelWithMetadataProperties>;

        if (!isValidElement(panelElement)) {
          return null;
        }

        const id = panelElement.props.metadata?.["@id"] ?? String(index);
        const isExpanded = openIds.includes(id);
        const isLastPanel = items.length - 1 === index;

        const updatedPanelElement = cloneElement(panelElement, {
          id,
          isExpanded,
          isLastPanel,
          setIsExpanded: allowMultipleOpen
            ? handleMultipleOpenPanels
            : handleSingleOpenPanel,
          variant,
        } as ExpansionPanelProperties);
        const updatedChildren = wrappedChildren.map(
          (wrappedChild, childIndex) =>
            childIndex === 1 ? updatedPanelElement : wrappedChild,
        );
        const updatedChild = cloneElement(child, undefined, updatedChildren);

        return <li key={id}>{updatedChild}</li>;
      })}
    </>
  );
};
